/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography, TypographyName } from '@zapier/design-system';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../constants';

export interface HeadingProps {
  backgroundColor:
    | typeof COLOR_KEYS.blue10
    | typeof COLOR_KEYS.cream
    | typeof COLOR_KEYS.neutral100
    | typeof COLOR_KEYS.neutral200;
  color: typeof COLOR_KEYS.earth | typeof COLOR_KEYS.neutral800;
  isCentered: boolean;
  maxWidth: 'small' | 'medium' | 'large' | 'none';
  size: 'small' | 'medium' | 'large';
  spacing: 'small' | 'medium' | 'large';
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  text: string;
}

export const SIZE_MAP: Record<
  HeadingProps['size'],
  Record<'mobile' | 'tablet' | 'desktop', TypographyName>
> = {
  small: {
    mobile: 'pageHeader9',
    tablet: 'pageHeader7',
    desktop: 'pageHeader5',
  },
  medium: {
    mobile: 'pageHeader7',
    tablet: 'pageHeader5',
    desktop: 'pageHeader3',
  },
  large: {
    mobile: 'pageHeader5',
    tablet: 'pageHeader3',
    desktop: 'pageHeader1',
  },
};

export const SPACING_MAP: Record<HeadingProps['spacing'], string> = {
  small: '0.5em',
  medium: '1.25em',
  large: '2em',
};

export const MAX_WIDTH_MAP: Record<HeadingProps['maxWidth'], string> = {
  small: '10ch',
  medium: '15ch',
  large: '20ch',
  none: 'none',
};

const Styles = labelStyles('Heading', {
  root: (backgroundColor: HeadingProps['backgroundColor']) =>
    css({
      backgroundColor: Colors[backgroundColor],
    }),

  container: css({
    maxWidth: '1170px',
    margin: '0 auto',
    padding: '0 20px',
  }),

  heading: (props: HeadingProps) =>
    css({
      ...Typography[SIZE_MAP[props.size].mobile],
      color: Colors[props.color],
      textAlign: props.isCentered ? 'center' : 'left',
      margin: props.isCentered ? '0 auto' : 0,
      padding: `${SPACING_MAP[props.spacing]} 0`,
      maxWidth: MAX_WIDTH_MAP[props.maxWidth],

      [MEDIA_MIN_WIDTH.small]: {
        ...Typography[SIZE_MAP[props.size].tablet],
      },

      [MEDIA_MIN_WIDTH.large]: {
        ...Typography[SIZE_MAP[props.size].desktop],
      },
    }),
});

export function Heading(props: HeadingProps) {
  const Tag = props.tag;
  return (
    <div css={Styles.root(props.backgroundColor)}>
      <div css={Styles.container}>
        <Tag css={Styles.heading(props)}>{props.text}</Tag>
      </div>
    </div>
  );
}
